import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Locale, TemplateVersion } from '@hlt/contracts';
import { apiKey, call, useApiQuery } from '@hlt/web-kit/api';
import { isLocale } from '@hlt/web-kit/i18n';
import { useSession } from '@hlt/web-kit/session';
import { useWorkspaceScope } from '@hlt/web-kit/shell';

export interface WorkspaceRef {
    organizationId: string;
    workspaceId: string;
}

/** Interface locale as a supported `Locale` (formatting and remark source language). */
export function useLocale(): Locale {
    const { i18n } = useTranslation();
    return isLocale(i18n.language) ? i18n.language : 'en';
}

/** Workspace metadata, the actor's role there and the effective member-facing tool controls. */
export function useWorkspaceContext(ref: WorkspaceRef) {
    const params = { organizationId: ref.organizationId, workspaceId: ref.workspaceId };
    const workspace = useApiQuery('getWorkspace', { params }, { staleTime: 60_000 });
    const access = useApiQuery(
        'getToolAccess',
        { params: { organizationId: ref.organizationId } },
        { staleTime: 60_000 },
    );
    const { scope, isPending } = useWorkspaceScope(ref.organizationId, ref.workspaceId);
    const session = useSession();
    const role = scope?.role ?? null;
    return {
        params,
        workspace: workspace.data ?? null,
        timezone: workspace.data?.timezone ?? 'UTC',
        role,
        canCreate: role === 'administrator' || role === 'consultant',
        isAdmin: role === 'administrator',
        userId: session.data?.userId ?? null,
        toolAccess: access.data ?? null,
        isPending: isPending || workspace.isPending || session.isPending,
    };
}

/** Minimal workspace directory (names only) for owner, collaborator and actor labels. */
export function usePeople(ref: WorkspaceRef) {
    const people = useApiQuery(
        'listWorkspacePeople',
        { params: { organizationId: ref.organizationId, workspaceId: ref.workspaceId }, input: { limit: 100 } },
        { staleTime: 60_000 },
    );
    const items = people.data?.items ?? [];
    const byId = new Map(items.map((p) => [p.userId, p]));
    return { items, byId, name: (userId: string) => byId.get(userId)?.name ?? null, isPending: people.isPending };
}

export interface VersionInfo {
    version: TemplateVersion;
    templateId: string;
    templateTitle: string;
}

/**
 * Every published template version of the workspace in one read, shared by audits and analytics for
 * template titles, version numbers, categories and scales. Workspaces with more than 100 versions read
 * further pages.
 */
export function templateVersionsQuery(ref: WorkspaceRef) {
    const params = { organizationId: ref.organizationId, workspaceId: ref.workspaceId };
    const input = { limit: 100 };
    return {
        queryKey: apiKey('listWorkspaceTemplateVersions', params, input),
        queryFn: async ({ signal }: { signal: AbortSignal }): Promise<TemplateVersion[]> => {
            const items: TemplateVersion[] = [];
            let cursor: string | undefined;
            for (let page = 0; page < 10; page++) {
                const result = await call('listWorkspaceTemplateVersions', {
                    params,
                    input: cursor ? { ...input, cursor } : input,
                    signal,
                });
                items.push(...result.items);
                if (!result.nextCursor) break;
                cursor = result.nextCursor;
            }
            return items;
        },
        staleTime: 5 * 60_000,
    };
}

/**
 * The workspace's templates and their published versions, read in parallel, so audits (which reference a
 * version) can show the template title and version number and filters can offer templates by identity.
 */
export function useTemplateCatalog(ref: WorkspaceRef) {
    const params = { organizationId: ref.organizationId, workspaceId: ref.workspaceId };
    const templates = useApiQuery('listTemplates', { params, input: { limit: 100 } }, { staleTime: 60_000 });
    const versions = useQuery(templateVersionsQuery(ref));
    const items = templates.data?.items ?? [];
    const published = items.filter((tpl) => tpl.activeVersionId !== null);
    const titles = new Map(items.map((tpl) => [tpl.id, tpl.title]));
    const byVersion = new Map<string, VersionInfo>();
    for (const version of versions.data ?? [])
        byVersion.set(version.id, {
            version,
            templateId: version.templateId,
            templateTitle: titles.get(version.templateId) ?? version.definition.title,
        });
    return {
        templates: items,
        published,
        byVersion,
        isPending: templates.isPending,
        error: templates.error,
        refetch: () => void templates.refetch(),
    };
}
